import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FareAdjustment, FareReviewDetail, FareReviewItem, FareReviewStatusUpdate } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class FareCalculationService extends UzurvRestService {
  readonly resourceUrl = 'fareCalculations/';

  constructor(
    protected httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    protected domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /* Fare Adjustment Review Queue */

  /**
   * Returns a list of fare review items for processing
   *
   * @param pageNumber
   * @param pageSize
   * @param stateCode
   * @permission fare_revew
   */
  listFareReviewItems(pageNumber: number, pageSize: number, stateCode?: string): Observable<Array<FareReviewItem>> {
    return this.get<Array<FareReviewItem>>(`${this.resourceUrl}reviews/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      stateCode: stateCode
    }).pipe(map(response => Deserialize(response, FareReviewItem)));
  }

  /**
   * Returns a dry run billing summary for a given fare adjustment. Used to give the
   * user feedback on what effect their fare adjustment will have on the billing summary
   *
   * @param fareAdjustment
   * @permission fare_revew
   */
  estimateFareAdjustment(fareAdjustment: FareAdjustment): Observable<FareReviewDetail> {
    return this.put<FareReviewDetail>(
      `${this.resourceUrl}estimateFareAdjustment/${fareAdjustment.reservationId}/`,
      Serialize(fareAdjustment, FareAdjustment)
    ).pipe(map(response => Deserialize(response, FareReviewDetail)));
  }

  /**
   * Updates the status of a fare review item and returns the updated reservation
   *
   * Valid options are:
   *
   * `Completed, Adjusted` - Complete review with adjustement, FareAdjustmentRO CAN NOT be null.
   *
   * `Completed, Not Adjusted` -  Complete review without adjustments to calculated fare, FareAdjustmentRO MUST be null
   *
   * `In Progress` -  Can transition both to and from "Required". Viewing a fare review SHOULD transition the
   *    status to "In Progress". Canceling out of a review without completing it SHOULD transition
   *    back to to "Required".
   *
   * `Required` -  CAN transition to "In Progress" or directly to one of the Completed statuses.
   *
   * @param fareReviewStatusUpdate
   * @permission fare_revew
   */
  updateFareReviewStatus(fareReviewStatusUpdate: FareReviewStatusUpdate): Observable<FareAdjustment> {
    return this.put<FareAdjustment>(
      `${this.resourceUrl}fareReviewStatus/${fareReviewStatusUpdate.reservationId}/`,
      Serialize(fareReviewStatusUpdate)
    ).pipe(map(response => Deserialize(response, FareAdjustment)));
  }
}
